import { useIonRouter, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import SequenceContent from "../../components/organisms/SequenceDetails/SequenceContent";
import SequenceQuestionnaire from "../../components/organisms/SequenceDetails/SequenceQuestionnaire";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useFetch } from "../../hooks/useFetch";
import {
  FullSequenceResponse,
  getApiFullSequence,
} from "../../services/questionnaire.service";
import { setSequenceComponentDone } from "../../services/sequence.service";
import { ComponentType } from "../../types/Questionnaire";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import { useRef } from 'react';

export const sequenceIsDone = (components: ComponentType[]): boolean => {
  return components.every((c) => c.is_done);
};

const SequenceComponent: FC<RouteComponentProps> = ({ history }) => {
  // Host detection is param-driven: `title` = island host, `groupKey` = the
  // investigator Suivi host, `tab === "calendar"` = the subject Calendrier host
  // (never name another param "title" — its mere presence flips URL generation
  // into the island shape).
  const { componentId, sequenceId, title, study_slug, groupKey, tab } = useParams<{
    componentId: string;
    sequenceId: string;
    study_slug: string;
    title: string;
    groupKey: string;
    tab: string;
  }>();
  const isCalendarHost = tab === "calendar";
  // Normalize once: the segment may arrive percent-encoded (free-text external_id
  // keys); always re-encode when building the next URL. Safe decode — a mangled
  // deep link must not throw URIError and white-screen the shared runner.
  const suiviGroupKey = useMemo(() => {
    if (!groupKey) return undefined;
    try {
      return decodeURIComponent(groupKey);
    } catch {
      return groupKey;
    }
  }, [groupKey]);

  const { setResonanceLayout } = useResonanceLayout();
  const { user } = useAuth();
  const { study } = useConfig();
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();

  const ionRouter = useIonRouter();
  const { updateScore, updateSequenceComponentDone, updateSequenceDone, fetchSequenceAvailability } =
    useResonanceData();

  useEffect(() => {
    console.log(
      "[SequenceComponent] mounted with params:",
      sequenceId,
      componentId
    );
  }, [sequenceId, componentId]);

  const { data, isLoading, isError } = useFetch<FullSequenceResponse>(
    `full-sequence:${sequenceId}`,
    getApiFullSequence,
    {
      studyId: study.id,
      userId: user.id,
      sequenceId: parseInt(sequenceId),
      lang: language,
    }
  );

  const sequence = data?.sequence;
  const answers = data?.answers?.answers ?? [];
  const component = useMemo(
    () => sequence?.components.find((c) => c.id === parseInt(componentId)),
    [sequence, componentId]
  );

  const questionnaire = useMemo(
    () =>
      data?.questionnaires?.find(
        (q) => String(q.id) === String(component?.questionnaire_id)
      ),
    [data?.questionnaires, component?.questionnaire_id]
  );

  const dependencies = useMemo(
    () =>
      questionnaire && data?.dependencies
        ? data.dependencies[questionnaire.id] ?? []
        : [],
    [questionnaire, data?.dependencies]
  );

  const qAnswers = useMemo(
    () =>
      answers.filter((a) => a.questionnaire_id === component?.questionnaire_id),
    [answers, component?.questionnaire_id]
  );

    useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: true,
      backgroundColor: "white",
      welcomePage: false,
    });
  });

  console.log("[SequenceComponent] full sequence data:", data);
  console.log("[SequenceComponent] looking for componentId:", componentId);
  console.log("[SequenceComponent] resolved component:", component);
  console.log("[SequenceComponent] resolved questionnaire:", questionnaire);

  useEffect(() => {
    console.log("[SequenceComponent] useEffect data changed", {
      data,
      isLoading,
      isError,
    });
  }, [data, isLoading, isError]);

  const currentId = component?.id ?? Number(componentId);

// If you want an extra guard against any flicker, keep the last good id:

const currentIdRef = useRef<number | null>(null);
if (component?.id && currentIdRef.current !== component.id) {
  currentIdRef.current = component.id;
}
const stableCurrentId = currentIdRef.current ?? currentId;

// Now compute index strictly from the resolved component, not params:
const componentIndex =
  sequence?.components.findIndex((c) => c.id === stableCurrentId) ?? -1;

// Find the next one (simple “next-in-order”):
const nextComponent =
  componentIndex >= 0 ? sequence!.components[componentIndex + 1] ?? null : null;

// If you prefer “skip done ones”:
const nextUndone =
  componentIndex >= 0
    ? sequence!.components.slice(componentIndex + 1).find((c) => !c.is_done) ?? null
    : null;

  /**
   * Landing route of the questionnaire's host surface (island / suivi group /
   * calendar / questionnaires list). Used as a deterministic fallback whenever
   * there is no back-stack entry to pop to — deep link, PWA cold-launch, page
   * reload, or after a component→component "replace" navigation — so exiting a
   * questionnaire can never dead-end.
   */
  const getHostPath = (): string => {
    if (suiviGroupKey)
      return `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(suiviGroupKey)}`;
    if (title) return `/${study_slug}/tabs/home/island/${title}`;
    if (isCalendarHost) return `/${study_slug}/tabs/calendar`;
    return `/${study_slug}/tabs/questionnaires`;
  };

  /**
   * Exit the questionnaire runner: pop back to wherever the user came from when
   * the router can, otherwise navigate straight to the host surface. This is the
   * fix for "Previous on the first question does nothing": the old fallback was
   * `history.goBack()`, which is a no-op (and can strand the user on the first
   * question) whenever there is no in-app history entry — e.g. after a reload,
   * deep link, or PWA launch directly onto the questionnaire. Pop back to the
   * still-mounted island/suivi/calendar page when possible so those hosts refetch
   * on re-entry; a router-visible replace can otherwise mount a duplicate page.
   */
  const exitToHost = () => {
    if (ionRouter.canGoBack()) {
      ionRouter.goBack();
    } else {
      history.replace(getHostPath());
    }
  };

  /** ✅ When all components of a sequence are done */
  const handleDone = () => {
    // Island / suivi group / calendar hosts keep a still-mounted page worth
    // popping back to; the questionnaires tab has none, so land on the list
    // directly. Both resolve to the host route when there's no back entry.
    if (suiviGroupKey || title || isCalendarHost) {
      exitToHost();
    } else {
      history.replace(getHostPath());
    }
  };

  /** ✅ Go to the next component */
  const handleNext = () => {
    if (nextComponent) {
      if (suiviGroupKey) {
        // From the Suivi group space — suiviGroupKey is the normalized (decoded)
        // key, re-encoded so free-text external_id keys survive the next URL.
        ionRouter.push(
          `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(suiviGroupKey)}/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      } else if (title) {
        // From home island
        ionRouter.push(
          `/${study_slug}/tabs/home/island/${title}/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      } else if (isCalendarHost) {
        // From the subject Calendrier
        ionRouter.push(
          `/${study_slug}/tabs/calendar/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      } else {
        // From questionnaires tab
        ionRouter.push(
          `/${study_slug}/tabs/questionnaires/sequences/${sequenceId}/components/${nextComponent.id}`,
          "forward",
          "replace"
        );
      }
    } else {
      handleDone();
    }
  };
  // const handleSequnceDone = () => {
  //   if (isLastSequence) {
  //     if (
  //       selectedRole.signin_criteria.method ===
  //       IdentificationMethodEnum.WITHOUT_IDENTIFICATION
  //     ) {
  //       endSession();
  //       history.replace(`/${study_slug}`);
  //     } else {
  //       history.replace(`/${study_slug}/home`);
  //     }
  //   } else {
  //     history.push(`/${study_slug}/sequences`);
  //   }
  // };

  // if (showSequenceDone)
  //   return <FinishedQuestionnaires onClick={handleSequnceDone} />;
  if (isLoading || !data) {
    return <ResonanceLoader />;
  }

  if (isError || !sequence) {
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
        onClick={() => ionRouter.canGoBack() && ionRouter.goBack()}
      />
    );
  }
  if (!component || !questionnaire) {
    return <ResonanceLoader />;
  }

  return (
    //this one is for showing the questionnaires indirectly
    // <SequenceContent
    //   studyId={study.id}
    //   contentId={component.content_id}
    //   sequenceId={parseInt(sequenceId)}
    //   onStartClick={handleNext}
    //   onPrevClick={() =>
    //     router.canGoBack() ? router.goBack() : history.goBack()
    //   }
    // />

    //this one is for showing the questionnaires directly
    <ResonanceLayout isConnected>
    <SequenceQuestionnaire
      key={`sq-${sequenceId}-${component.id}`}
      questionnaireId={component.questionnaire_id}
      questionnaireTitle={component.questionnaire_title}
      questionnaireData={questionnaire}
      answers={qAnswers}
      dependencies={dependencies}
      componentId={component.id}
      onNextClick={handleNext}
      // "Previous" on the first question exits the questionnaire. exitToHost
      // always lands somewhere (host page fallback) instead of the old
      // history.goBack() no-op that left users stuck on the first question.
      onPrevClick={exitToHost}
      onQuestionnaireDone={async (totalScore) => {
        const { data } = await setSequenceComponentDone(
          {
            studyId: study.id,
            userId: user.id,
            sequenceId,
            sequenceComponentId: component.id,
          },
          totalScore
        );

        // update local scores
        if (component.is_scored && totalScore !== null) {
          updateScore(
            data.userSequenceComponentId,
            data.userSequenceId,
            data.sequenceId,
            data.sequenceComponentId,
            data.questionnaireId,
            data.score
          );
        }

        // update local sequence/component status
        updateSequenceComponentDone(
          data.userSequenceId,
          data.sequenceComponentId
        );
        if (data.allDone) {
          updateSequenceDone(data.userSequenceId);
          await fetchSequenceAvailability();
        }
      }}
    />
    </ResonanceLayout>
  );
};

export default SequenceComponent;
